import {
  IFailedCustomMappingRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Firestore,
  XSLXImporterExporter,
  resolveWithConcurrencyForAll,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { sortBy, uniqBy } from 'lodash';
import { DELETE_CONCURRENCY } from '../../../base-custom-mapping-handler';
import { IBaseMigrationItemCode } from '../../../interfaces';
import { BaseItemCodeToConditionMappingHandler } from '../../../mappings/base-item-code-to-condition';
import { XLSXToItemCodeToCondition } from '../../../mappings/xlsx-to-item-code-to-condition';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import {
  CorePracticeItemCode,
  ICorePracticeItemCode,
  ItemCodeSourceEntity,
} from '../../source/entities/item-codes';

interface ISourceItemInfo {
  sourceIdentifier: string;
  sourceLabel: string;
}

export class CorePracticeItemCodeToConditionMappingHandler extends BaseItemCodeToConditionMappingHandler<
  CorePracticeItemCode,
  ItemCodeSourceEntity
> {
  sourceEntity = new ItemCodeSourceEntity();
  translateFn = (record: ICorePracticeItemCode): IBaseMigrationItemCode => ({
    id: record.id,
    itemCode: record.itemCode,
    description: record.name ?? '',
  });

  /**
   * Core's tblChartItem rows reference tblItem by ItemId, and multiple ItemIds
   * can share the same item code. Fan a single uploaded code out to every
   * sharing ItemId so each charted item resolves its condition. Scoped to Core,
   * not the base, because Core's source is the only one that yields several
   * distinct ids per item code. Exact/Oasis/PracticeWorks emit exactly one id
   * per code (the id IS the item code), so fan-out is a no-op there; D4W
   * (item_id != item code) could differ but is deliberately left on the base
   * last-write-wins.
   */
  override async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XLSXToItemCodeToCondition()
    );

    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await resolveWithConcurrencyForAll(records, DELETE_CONCURRENCY, (record) =>
      translationMap.deleteRecord(record)
    );

    const itemCodes = await this.getItemCodes(migration);
    const codeToSourceInfos = new Map<string, ISourceItemInfo[]>();
    itemCodes.forEach((itemCode) => {
      const key = itemCode.itemCode.trim();
      const info: ISourceItemInfo = {
        sourceIdentifier: itemCode.id.toString(),
        sourceLabel: this._buildLabel(itemCode),
      };
      const existing = codeToSourceInfos.get(key);
      if (existing) {
        existing.push(info);
      } else {
        codeToSourceInfos.set(key, [info]);
      }
    });

    const conditionConfigurations = await this.getDestinationOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    const recordsForUpload = items.flatMap((item) => {
      const code = item.code?.trim();
      if (!code) {
        return [];
      }

      const sourceInfos = codeToSourceInfos.get(code);
      const label = item.description
        ? `${item.code} - ${item.description}`
        : item.code;

      if (!sourceInfos && item.mapTo) {
        failedRecords.push({
          sourceIdentifier: code,
          sourceLabel: label,
          reason: 'Item code not found in source options',
        });
        return [];
      }

      if (!sourceInfos || !item.mapTo) {
        return [];
      }

      const conditionConfiguration = conditionConfigurations.find(
        (configuration) => configuration.name === item.mapTo
      );

      if (!conditionConfiguration) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find condition configuration for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: code,
          sourceLabel: label,
          reason: `Condition configuration not found: ${item.mapTo}`,
        });
        return [];
      }

      return sourceInfos.map((sourceInfo) => ({
        destinationIdentifier: conditionConfiguration.ref,
        sourceIdentifier: sourceInfo.sourceIdentifier,
        sourceLabel: sourceInfo.sourceLabel,
      }));
    });

    const dedupedRecords = uniqBy(
      recordsForUpload,
      (record) => record.sourceIdentifier
    );
    const results = await this.upsertBulk(dedupedRecords, translationMap);

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords: [...failedRecords, ...results.failedRecords],
    });
  }

  // One row per code in the download template (Core has duplicate codes across
  // ItemIds); the upload fan-out re-expands to every ItemId.
  protected override _prepareExporterItemCodes(
    options: IBaseMigrationItemCode[]
  ): IBaseMigrationItemCode[] {
    return uniqBy(sortBy(options, 'itemCode'), (option) =>
      option.itemCode.trim()
    );
  }
}
